/**
 * MultiTypeMapping renders the column-mapping UI for a CSV file that contains
 * multiple object types (e.g. contacts + deals in one file).
 *
 * It reuses the SAME rich per-type section as single-type files
 * (`FileMappingSection`) — one section per entity type — so the mapping
 * experience (per-column Property / Link to record / Do not import, field
 * types, stage handling, hide toggle, entity-type icon header) is identical
 * across single- and multi-type imports. Each entity type is treated as a
 * "virtual file" keyed by `${fileId}_virtual_${entityTypeName}`.
 */

import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { DraggableItem, DropArea } from '@/lib/base/card';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { AssociationMatcher } from '@/lib/crm/hooks/useAssociationMatchers';
import type {
  ColumnMapping,
  ColumnMappings,
  CrmEntityAssociationMapping,
  EntityTypeMappingSuggestion,
  FixedValueMapping,
  MultiTypeMappingSuggestion,
  NewPropertySuggestion,
  StageMappings,
} from '@/lib/csv-import/api';

import type { AssociationTypeSelections } from '../../csv-import-helpers';
import {
  getRenderedEntityTypeNames,
  moveColumnBetweenTypes,
  unassignedVirtualKey,
} from '../../csv-import-helpers';
import type { ImportTypeOption } from '../../hooks/useImportTargetTypes';
import { SKIP_MARKER } from '../../useCsvImport';
import type { MoveTarget } from './ColumnActionsMenu';
import { EntityTypePill } from './EntityTypePill';
import { FileDefaultsPanel } from './FileDefaultsPanel';
import { FileMappingSection } from './FileMappingSection';
import { InternalColumnsRow } from './InternalColumnsRow';
import {
  buildNewPropertyIndex,
  type FileColumnInfo,
  propertyIndexKey,
} from './mapping-constants';
import { SectionAssociations } from './SectionAssociations';
import { UnassignedColumnsRow } from './UnassignedColumnsRow';

// Stable empty selections object for files with no association choices yet.
const EMPTY_SELECTIONS: Record<string, string> = {};

// ============================================================================
// MultiTypeEntitySection — one rich section per entity type (virtual file)
// ============================================================================

interface MultiTypeEntitySectionProps {
  entityType: EntityTypeMappingSuggestion;
  fileId: string | number;
  filename: string;
  orgId: number;
  /** This section's effective column→property map (override ?? suggestion). */
  effectiveMappings: ColumnMapping;
  /** Columns shown in this section (keys of `effectiveMappings`, sorted). */
  typeColumns: string[];
  /** Association controls rendered as a row under this section's mappings. */
  sectionAssociations?: ReactNode;
  handleMappingChange: (
    virtualFileId: string | number,
    csvColumn: string,
    propertyName: string
  ) => void;
  handleRestoreMapping: (
    virtualFileId: string | number,
    csvColumn: string
  ) => void;
  isExpanded: boolean;
  onExpandedChange: (entityTypeName: string, isOpen: boolean) => void;
  stageMappings: StageMappings;
  onStageMappingsChange: Dispatch<SetStateAction<StageMappings>>;
  fixedValueMappings: FixedValueMapping[];
  onFixedValueMappingsChange: (mappings: FixedValueMapping[]) => void;
  crmEntityAssociationMappings: CrmEntityAssociationMapping[];
  onCrmEntityAssociationMappingsChange: (
    mappings: CrmEntityAssociationMapping[]
  ) => void;
  targetTypeOptions: ImportTypeOption[];
  associationTypes: AssociationType[];
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  errorProperties?: Record<string, string>;
  onConfigureSelectOptions?: (
    propertyName: string,
    entityTypeName: string
  ) => void;
  /**
   * Forwarded to each entity-type `FileMappingSection`, which binds its own
   * virtual editKey as `mappingKey` (see `FileMappingSectionProps`).
   */
  onCreateProperty?: (
    mappingKey: string | number,
    csvColumn: string,
    label: string
  ) => void;
  onNewPropertyFieldTypeChange?: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newFieldType: string
  ) => void;
  /** Column name currently being dragged (highlights the source row). */
  draggedColumn?: string | null;
  /** Entity types this section's columns can be moved to (excludes self). */
  moveTargets?: MoveTarget[];
  /** Called when the user picks "Remove" from a row's action menu. */
  onRemoveColumn?: (column: string) => void;
  /** Called when the user picks "Move to {type}" from a row's action menu. */
  onMoveColumn?: (column: string, targetVirtualKey: string) => void;
  matchColumns: Record<string, string[]>;
  onMatchColumnToggle: (
    fileId: string | number,
    csvColumn: string,
    enabled: boolean
  ) => void;
  /** CSV columns used as cross-file association keys for this file. */
  associationKeyColumns?: ReadonlySet<string>;
  /**
   * User-created new properties, keyed by plain file id (the shape
   * `usePropertyManagement` stores). Merged with this entity type's
   * AI-suggested `new_properties` so a property the user just created is
   * resolvable by the mapping select — without it the select can't find the
   * option and falls back to formatting the raw property_name slug.
   */
  userNewPropertiesByFile?: Record<string, NewPropertySuggestion[]>;
}

function MultiTypeEntitySection({
  entityType,
  fileId,
  filename,
  orgId,
  effectiveMappings,
  typeColumns,
  sectionAssociations,
  handleMappingChange,
  handleRestoreMapping,
  isExpanded,
  onExpandedChange,
  stageMappings,
  onStageMappingsChange,
  fixedValueMappings,
  onFixedValueMappingsChange,
  crmEntityAssociationMappings,
  onCrmEntityAssociationMappingsChange,
  targetTypeOptions,
  associationTypes,
  matchesSource,
  matchesTarget,
  errorProperties,
  onConfigureSelectOptions,
  onCreateProperty,
  onNewPropertyFieldTypeChange,
  draggedColumn,
  moveTargets,
  onRemoveColumn,
  onMoveColumn,
  matchColumns,
  onMatchColumnToggle,
  associationKeyColumns,
  userNewPropertiesByFile,
}: MultiTypeEntitySectionProps) {
  const entityTypeName = entityType.entity_type_name;
  const virtualKey = `${fileId}_virtual_${entityTypeName}`;

  const { setNodeRef, isOver } = useDroppable({ id: virtualKey });

  const fileLike = useMemo<FileColumnInfo>(
    () => ({
      fileId: virtualKey,
      filename,
      entityTypeName,
      columns: typeColumns,
    }),
    [virtualKey, filename, entityTypeName, typeColumns]
  );

  const handleSectionExpanded = useCallback(
    (_fileId: string | number, isOpen: boolean) =>
      onExpandedChange(entityTypeName, isOpen),
    [onExpandedChange, entityTypeName]
  );

  const handleFieldTypeChange = useCallback(
    (propertyName: string, newFieldType: string) =>
      onNewPropertyFieldTypeChange?.(
        fileId,
        entityTypeName,
        propertyName,
        newFieldType
      ),
    [onNewPropertyFieldTypeChange, fileId, entityTypeName]
  );

  // AI-suggested new properties for this entity type, plus the ones the user
  // created here. `usePropertyManagement` stores user properties under the
  // PLAIN file id (not the virtual key) and stamps the entity type on each, so
  // select this type's subset by `entity_type_name`. Without this merge the
  // mapping select has no option matching the new property_name and falls back
  // to slug-formatting it — "MRR (native)" renders as "Mrr Native".
  //
  // When the same property_name appears in both lists, the user's entry wins
  // when its label is non-empty (whitespace-only labels fall through to the AI
  // entry). This mirrors the identical semantics in `buildNewPropertyIndex`,
  // keeping the mapping table and the preview card / New Properties panel in
  // sync. AI ordering is preserved for the dropdown.
  const userNewProperties = useMemo<NewPropertySuggestion[]>(() => {
    const aiSuggested = entityType.new_properties ?? [];
    const userCreated = (
      userNewPropertiesByFile?.[String(fileId)] ?? []
    ).filter(np => np.entity_type_name === entityTypeName);
    if (userCreated.length === 0) {
      return aiSuggested;
    }
    const index = buildNewPropertyIndex(userCreated, aiSuggested);
    const aiPropertyNames = new Set(aiSuggested.map(np => np.property_name));
    return [
      ...aiSuggested.map(
        np =>
          index.get(propertyIndexKey(np.property_name, entityTypeName)) ?? np
      ),
      ...userCreated.filter(
        np => !aiPropertyNames.has(np.property_name) && Boolean(np.label.trim())
      ),
    ];
  }, [
    entityType.new_properties,
    userNewPropertiesByFile,
    fileId,
    entityTypeName,
  ]);

  return (
    <DropArea nodeRef={setNodeRef} isOver={isOver}>
      <FileMappingSection
        file={fileLike}
        orgId={orgId}
        mappings={effectiveMappings}
        handleMappingChange={handleMappingChange}
        onRestoreMapping={handleRestoreMapping}
        isExpanded={isExpanded}
        onExpandedChange={handleSectionExpanded}
        matchColumns={matchColumns}
        onMatchColumnToggle={onMatchColumnToggle}
        fileId={virtualKey}
        onCreateProperty={onCreateProperty}
        userNewProperties={userNewProperties}
        onUserNewPropertyFieldTypeChange={handleFieldTypeChange}
        errorProperties={errorProperties}
        onConfigureSelectOptions={onConfigureSelectOptions}
        crmEntityAssociationMappings={crmEntityAssociationMappings}
        onCrmEntityAssociationMappingsChange={
          onCrmEntityAssociationMappingsChange
        }
        targetTypeOptions={targetTypeOptions}
        associationTypes={associationTypes}
        matchesSource={matchesSource}
        matchesTarget={matchesTarget}
        dragSourceId={virtualKey}
        draggedColumn={draggedColumn}
        moveTargets={moveTargets}
        onRemoveColumn={onRemoveColumn}
        onMoveColumn={onMoveColumn}
        defaultStage={stageMappings[virtualKey]?.default_stage}
        sectionDefaults={
          <FileDefaultsPanel
            orgId={orgId}
            entityType={entityTypeName}
            fileId={virtualKey}
            fixedValueMappings={fixedValueMappings}
            onFixedValueMappingsChange={onFixedValueMappingsChange}
            stageMappings={stageMappings}
            onStageMappingsChange={onStageMappingsChange}
          />
        }
        sectionAssociations={sectionAssociations}
        associationKeyColumns={associationKeyColumns}
      />
    </DropArea>
  );
}

// ============================================================================
// MultiTypeMapping
// ============================================================================

export interface MultiTypeMappingProps {
  orgId: number;
  fileId: string | number;
  filename: string;
  multiTypeSuggestion: MultiTypeMappingSuggestion;
  columnMappings: ColumnMappings;
  onColumnMappingsChange: (mappings: ColumnMappings) => void;
  columns: string[];
  onNewPropertyFieldTypeChange?: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newFieldType: string
  ) => void;
  /** Forwarded to `FileMappingSection`, which binds its own virtual editKey. */
  onCreateProperty?: (
    mappingKey: string | number,
    csvColumn: string,
    label: string
  ) => void;
  stageMappings: StageMappings;
  onStageMappingsChange: Dispatch<SetStateAction<StageMappings>>;
  matchColumns: Record<string, string[]>;
  onMatchColumnToggle: (
    fileId: string | number,
    csvColumn: string,
    enabled: boolean
  ) => void;
  fixedValueMappings: FixedValueMapping[];
  onFixedValueMappingsChange: (mappings: FixedValueMapping[]) => void;
  crmEntityAssociationMappings: CrmEntityAssociationMapping[];
  onCrmEntityAssociationMappingsChange: (
    mappings: CrmEntityAssociationMapping[]
  ) => void;
  /** Full association-target set (incl. ahoy_user) — used for "Link to record". */
  targetTypeOptions: ImportTypeOption[];
  /**
   * Entity types the importing user may create — the set offered as importable
   * "slots" (pills). Already permission-filtered and excludes link-only targets.
   */
  importableTypeOptions: ImportTypeOption[];
  associationTypes: AssociationType[];
  matchesSource: AssociationMatcher;
  matchesTarget: AssociationMatcher;
  /** Analysis errors keyed by virtual file id → property name → message. */
  errorPropertiesByFile?: Record<string, Record<string, string>>;
  onConfigureSelectOptions?: (
    propertyName: string,
    entityTypeName: string
  ) => void;
  /**
   * Registers an entity type in the data model so its mapped columns are
   * submitted. Called automatically the first time a column lands on a type's
   * pill. Idempotent (no-ops if the type is already present).
   */
  onAddEntityType?: (fileId: string | number, entityTypeName: string) => void;
  /** Chosen association types per directed section pair (`${src}|${tgt}`). */
  associationTypeSelections: AssociationTypeSelections;
  /** Set/disable the association type for a directed (above→below) section pair. */
  onAssociationTypeSelectionChange: (
    fileId: string | number,
    sourceType: string,
    targetType: string,
    associationTypeName: string
  ) => void;
  /** CSV columns used as cross-file association keys for this file. */
  associationKeyColumns?: ReadonlySet<string>;
  /** User-created new properties, keyed by plain file id. */
  userNewProperties?: Record<string, NewPropertySuggestion[]>;
}

export function MultiTypeMapping({
  orgId,
  fileId,
  filename,
  multiTypeSuggestion,
  columnMappings,
  onColumnMappingsChange,
  columns,
  onNewPropertyFieldTypeChange,
  onCreateProperty,
  stageMappings,
  onStageMappingsChange,
  matchColumns,
  onMatchColumnToggle,
  fixedValueMappings,
  onFixedValueMappingsChange,
  crmEntityAssociationMappings,
  onCrmEntityAssociationMappingsChange,
  targetTypeOptions,
  importableTypeOptions,
  associationTypes,
  matchesSource,
  matchesTarget,
  errorPropertiesByFile,
  onConfigureSelectOptions,
  onAddEntityType,
  associationTypeSelections,
  onAssociationTypeSelectionChange,
  associationKeyColumns,
  userNewProperties,
}: MultiTypeMappingProps) {
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(() => {
    const first = multiTypeSuggestion.entity_types.find(
      et => Object.keys(et.mappings).length > 0
    );
    return first ? new Set([first.entity_type_name]) : new Set();
  });

  const virtualPrefix = `${fileId}_virtual_`;

  // The complete set of entity types the file can map to: the AI-suggested /
  // already-mapped types from the data model, plus a synthetic slot for every
  // other importable type (so each one is always present, rendered as a pill
  // until columns land on it). `ahoy_user` is link-only and never a slot.
  const allEntityTypes = useMemo<EntityTypeMappingSuggestion[]>(() => {
    const existing = new Set(
      multiTypeSuggestion.entity_types.map(et => et.entity_type_name)
    );
    // Synthetic pill slots: every importable type the user can create that
    // isn't already an active/suggested section. `importableTypeOptions` is
    // permission-filtered and excludes link-only targets (ahoy_user).
    const synthetic = importableTypeOptions
      .filter(opt => !existing.has(opt.value))
      .map(opt => ({
        entity_type_name: opt.value,
        mappings: {},
        new_properties: [],
      }));
    return [...multiTypeSuggestion.entity_types, ...synthetic];
  }, [multiTypeSuggestion.entity_types, importableTypeOptions]);

  const labelByTypeName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of targetTypeOptions) {
      map[opt.value] = opt.label;
    }
    return map;
  }, [targetTypeOptions]);

  const allColumns = useMemo(() => [...columns].sort(), [columns]);

  const unassignedKey = useMemo(() => unassignedVirtualKey(fileId), [fileId]);

  // Columns whose header matches a system-managed property the backend refuses to
  // map (e.g. the ahoy_last_activity_at rollup). Shown read-only in their own
  // section — NOT as unassigned, which would imply the user should act on them.
  const internalColumns = useMemo(
    () => multiTypeSuggestion.internal_columns ?? [],
    [multiTypeSuggestion.internal_columns]
  );
  const internalColumnSet = useMemo(
    () => new Set(internalColumns),
    [internalColumns]
  );

  // Each type's effective column→property map (override ?? AI suggestion).
  // Mirrors the serializer, so dragging a column out (deleting its key) removes
  // it from that section and dragging one in (adding a key) surfaces it. Internal
  // (system-managed) columns are stripped here so a stale or resumed mapping can
  // never surface them as editable rows or feed them into a section — they live
  // exclusively in the read-only Internal columns section.
  const effectiveByVirtualKey = useMemo(() => {
    const map: Record<string, ColumnMapping> = {};
    for (const et of allEntityTypes) {
      const vk = `${virtualPrefix}${et.entity_type_name}`;
      const eff = columnMappings[vk] ?? et.mappings;
      map[vk] = internalColumnSet.size
        ? Object.fromEntries(
            Object.entries(eff).filter(
              ([column]) => !internalColumnSet.has(column)
            )
          )
        : eff;
    }
    return map;
  }, [allEntityTypes, virtualPrefix, columnMappings, internalColumnSet]);

  // Columns shown in each section = keys of its effective map (sorted by the
  // shared column order so sections stay consistent with each other).
  const typeColumnsByVirtualKey = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const [vk, eff] of Object.entries(effectiveByVirtualKey)) {
      map[vk] = allColumns.filter(c => Object.hasOwn(eff, c));
    }
    return map;
  }, [effectiveByVirtualKey, allColumns]);

  // The Unassigned fallback bucket: columns in no section's effective map. This
  // guarantees every column is always visible — when a column's mapping is
  // cleared or it is dragged out of every section, it lands here rather than
  // silently disappearing. Internal columns are excluded (they have their own
  // read-only section).
  const unassignedColumns = useMemo(() => {
    const assigned = new Set<string>(internalColumns);
    for (const cols of Object.values(typeColumnsByVirtualKey)) {
      for (const c of cols) {
        assigned.add(c);
      }
    }
    return allColumns.filter(c => !assigned.has(c));
  }, [typeColumnsByVirtualKey, allColumns, internalColumns]);

  // Write a user override for a column (SKIP_MARKER when cleared to '').
  const handleMappingChange = useCallback(
    (
      virtualFileId: string | number,
      csvColumn: string,
      propertyName: string
    ) => {
      const key = String(virtualFileId);
      const currentFileMappings = columnMappings[key] ?? {};
      const newFileMappings: ColumnMapping = {
        ...currentFileMappings,
        [csvColumn]: propertyName === '' ? SKIP_MARKER : propertyName,
      };
      onColumnMappingsChange({ ...columnMappings, [key]: newFileMappings });
    },
    [columnMappings, onColumnMappingsChange]
  );

  // Restore (unskip) drops the user override so the AI-suggested mapping in
  // entityType.mappings[col] becomes effective again.
  const handleRestoreMapping = useCallback(
    (virtualFileId: string | number, csvColumn: string) => {
      const key = String(virtualFileId);
      const currentFileMappings = columnMappings[key] ?? {};
      if (!(csvColumn in currentFileMappings)) {
        return;
      }
      const { [csvColumn]: _removed, ...rest } = currentFileMappings;
      onColumnMappingsChange({ ...columnMappings, [key]: rest });
    },
    [columnMappings, onColumnMappingsChange]
  );

  const handleEntityTypeExpandedChange = useCallback(
    (entityTypeName: string, isOpen: boolean) => {
      setExpandedTypes(prev => {
        const next = new Set(prev);
        if (isOpen) {
          next.add(entityTypeName);
        } else {
          next.delete(entityTypeName);
        }
        return next;
      });
    },
    []
  );

  // --- Drag a column from one entity-type section to another (MOVE) ----------

  // virtual file id -> that type's AI-suggested mappings, for materializing the
  // effective map when a section has no override yet. Covers synthetic (pill)
  // types too so a column can be dropped onto a not-yet-mapped type.
  const suggestionByVirtualKey = useMemo(() => {
    const map: Record<string, ColumnMapping> = {};
    for (const et of allEntityTypes) {
      map[`${virtualPrefix}${et.entity_type_name}`] = et.mappings;
    }
    return map;
  }, [allEntityTypes, virtualPrefix]);

  // Entity-type names already present in the data model (vs synthetic pills).
  const registeredTypeNames = useMemo(
    () =>
      new Set(multiTypeSuggestion.entity_types.map(et => et.entity_type_name)),
    [multiTypeSuggestion.entity_types]
  );

  const sensors = useSensors(
    // 8px activation distance so a click on the handle doesn't start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const [activeColumn, setActiveColumn] = useState<string | null>(null);

  const moveColumn = useCallback(
    (sourceVk: string, targetVk: string, column: string) => {
      onColumnMappingsChange(
        moveColumnBetweenTypes(
          columnMappings,
          suggestionByVirtualKey,
          sourceVk,
          targetVk,
          column,
          unassignedKey
        )
      );
      // A column leaving its section takes any "Link to record" mapping with it:
      // the link is keyed to (sourceVk, column). Mirror FileMappingSection's
      // handleRemoveLink. Skip same-section drops (the move is a no-op there) so a
      // column dropped back on its own section keeps its link; only fire when a
      // mapping was actually removed.
      if (sourceVk !== targetVk) {
        const nextLinks = crmEntityAssociationMappings.filter(
          m =>
            !(
              String(m.source_file_id) === sourceVk &&
              m.source_column === column
            )
        );
        if (nextLinks.length !== crmEntityAssociationMappings.length) {
          onCrmEntityAssociationMappingsChange(nextLinks);
        }
      }
    },
    [
      columnMappings,
      suggestionByVirtualKey,
      onColumnMappingsChange,
      unassignedKey,
      crmEntityAssociationMappings,
      onCrmEntityAssociationMappingsChange,
    ]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = String(event.active.id);
    const sep = id.indexOf('::');
    setActiveColumn(sep >= 0 ? id.slice(sep + 2) : null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveColumn(null);
      const { active, over } = event;
      if (!over) {
        return;
      }
      const activeId = String(active.id);
      const sep = activeId.indexOf('::');
      if (sep < 0) {
        return;
      }
      const sourceVk = activeId.slice(0, sep);
      const targetVk = String(over.id);
      const column = activeId.slice(sep + 2);

      // Dropping onto a type slot (not the Unassigned bucket): if the target is
      // a synthetic pill, register it in the data model so its columns submit,
      // and auto-expand it as it becomes a section.
      if (targetVk !== unassignedKey && targetVk.startsWith(virtualPrefix)) {
        const entityTypeName = targetVk.slice(virtualPrefix.length);
        if (!registeredTypeNames.has(entityTypeName)) {
          onAddEntityType?.(fileId, entityTypeName);
        }
        if ((typeColumnsByVirtualKey[targetVk]?.length ?? 0) === 0) {
          setExpandedTypes(prev => new Set([...prev, entityTypeName]));
        }
      }

      moveColumn(sourceVk, targetVk, column);
    },
    [
      moveColumn,
      unassignedKey,
      virtualPrefix,
      registeredTypeNames,
      onAddEntityType,
      fileId,
      typeColumnsByVirtualKey,
    ]
  );

  // Split the full type set into active sections (have columns) and pills (no
  // columns). Rendering is purely a function of mappings — no add/remove modes.
  // The active set comes from the shared getRenderedEntityTypeNames helper, the
  // single source of truth also used by the sidebar label and validation, so a
  // type appears/vanishes identically everywhere. As a safety net we also render
  // any type (including a synthetic pill) that already has columns parked in its
  // effective map — a column dragged onto a not-yet-registered synthetic type
  // would otherwise be hidden (it's assigned, so it isn't in Unassigned either)
  // until onAddEntityType lands it in the suggestion's declared entity_types.
  const renderedTypeNames = new Set([
    ...getRenderedEntityTypeNames(multiTypeSuggestion, columnMappings, fileId),
    ...allEntityTypes
      .filter(
        et =>
          (typeColumnsByVirtualKey[`${virtualPrefix}${et.entity_type_name}`]
            ?.length ?? 0) > 0
      )
      .map(et => et.entity_type_name),
  ]);
  const activeEntityTypes = allEntityTypes.filter(et =>
    renderedTypeNames.has(et.entity_type_name)
  );
  const pillEntityTypes = allEntityTypes.filter(
    et => !renderedTypeNames.has(et.entity_type_name)
  );

  const isMultiType = activeEntityTypes.length > 1;

  const fileSelections = associationTypeSelections[fileId] ?? EMPTY_SELECTIONS;

  // Move targets for the per-row action menu: all entity types (active sections
  // AND pill-only types), so the user can move a column to any type — including
  // one that has no columns yet (which promotes it to a full section, same as
  // drag-and-drop onto a pill). Each section filters out itself below.
  const allMoveTargets = useMemo<MoveTarget[]>(
    () =>
      allEntityTypes.map(et => ({
        entityTypeName: et.entity_type_name,
        label: labelByTypeName[et.entity_type_name] ?? et.entity_type_name,
        virtualKey: `${virtualPrefix}${et.entity_type_name}`,
      })),
    [allEntityTypes, labelByTypeName, virtualPrefix]
  );

  // "Remove" moves the column to the unassigned pool.
  const handleRemoveColumn = useCallback(
    (column: string) => {
      // Find which section owns this column and move it to unassigned.
      for (const [vk, cols] of Object.entries(typeColumnsByVirtualKey)) {
        if (cols.includes(column)) {
          moveColumn(vk, unassignedKey, column);
          return;
        }
      }
    },
    [typeColumnsByVirtualKey, moveColumn, unassignedKey]
  );

  // "Move to {type}" moves the column from its current section to the target.
  const handleMoveColumn = useCallback(
    (column: string, targetVirtualKey: string) => {
      // Find which section (or unassigned bucket) currently owns this column.
      let sourceVk: string | null = null;
      for (const [vk, cols] of Object.entries(typeColumnsByVirtualKey)) {
        if (cols.includes(column)) {
          sourceVk = vk;
          break;
        }
      }
      // Column not in any section → it's in the unassigned pool.
      if (!sourceVk) {
        sourceVk = unassignedKey;
      }

      // Register the target type if it's synthetic (same as drag-and-drop).
      if (targetVirtualKey.startsWith(virtualPrefix)) {
        const targetTypeName = targetVirtualKey.slice(virtualPrefix.length);
        if (!registeredTypeNames.has(targetTypeName)) {
          onAddEntityType?.(fileId, targetTypeName);
        }
        if ((typeColumnsByVirtualKey[targetVirtualKey]?.length ?? 0) === 0) {
          setExpandedTypes(prev => new Set([...prev, targetTypeName]));
        }
      }
      moveColumn(sourceVk, targetVirtualKey, column);
    },
    [
      typeColumnsByVirtualKey,
      moveColumn,
      virtualPrefix,
      registeredTypeNames,
      onAddEntityType,
      fileId,
      unassignedKey,
    ]
  );

  // Precompute each active type's move targets and association sources once per
  // render — stable references so sections whose inputs haven't changed don't
  // pass new array/object props to their children (see FRONTEND_PATTERNS.md
  // "Stable References").
  const sectionDataByTypeName = useMemo(() => {
    const result = new Map<
      string,
      {
        sectionMoveTargets: MoveTarget[];
        aboveTypes: { name: string; label: string }[];
      }
    >();
    activeEntityTypes.forEach((entityType, index) => {
      const sectionMoveTargets = allMoveTargets.filter(
        t => t.entityTypeName !== entityType.entity_type_name
      );
      // Sections above this one are the potential association sources (above =
      // source, this row = target).
      const aboveTypes = activeEntityTypes.slice(0, index).map(et => ({
        name: et.entity_type_name,
        label: labelByTypeName[et.entity_type_name] ?? et.entity_type_name,
      }));
      result.set(entityType.entity_type_name, {
        sectionMoveTargets,
        aboveTypes,
      });
    });
    return result;
  }, [activeEntityTypes, allMoveTargets, labelByTypeName]);

  const sections = activeEntityTypes.map(entityType => {
    const virtualKey = `${virtualPrefix}${entityType.entity_type_name}`;
    const typeColumns = typeColumnsByVirtualKey[virtualKey] ?? [];
    const { sectionMoveTargets, aboveTypes } = sectionDataByTypeName.get(
      entityType.entity_type_name
    ) ?? { sectionMoveTargets: [], aboveTypes: [] };
    return (
      <MultiTypeEntitySection
        key={entityType.entity_type_name}
        entityType={entityType}
        userNewPropertiesByFile={userNewProperties}
        fileId={fileId}
        filename={filename}
        orgId={orgId}
        effectiveMappings={effectiveByVirtualKey[virtualKey] ?? {}}
        typeColumns={typeColumns}
        sectionAssociations={
          aboveTypes.length > 0 ? (
            <SectionAssociations
              fileId={fileId}
              targetType={entityType.entity_type_name}
              targetLabel={
                labelByTypeName[entityType.entity_type_name] ??
                entityType.entity_type_name
              }
              aboveTypes={aboveTypes}
              associationTypes={associationTypes}
              matchesSource={matchesSource}
              matchesTarget={matchesTarget}
              fileSelections={fileSelections}
              onChange={onAssociationTypeSelectionChange}
            />
          ) : undefined
        }
        handleMappingChange={handleMappingChange}
        handleRestoreMapping={handleRestoreMapping}
        isExpanded={expandedTypes.has(entityType.entity_type_name)}
        onExpandedChange={handleEntityTypeExpandedChange}
        stageMappings={stageMappings}
        onStageMappingsChange={onStageMappingsChange}
        matchColumns={matchColumns}
        onMatchColumnToggle={onMatchColumnToggle}
        fixedValueMappings={fixedValueMappings}
        onFixedValueMappingsChange={onFixedValueMappingsChange}
        crmEntityAssociationMappings={crmEntityAssociationMappings}
        onCrmEntityAssociationMappingsChange={
          onCrmEntityAssociationMappingsChange
        }
        targetTypeOptions={targetTypeOptions}
        associationTypes={associationTypes}
        matchesSource={matchesSource}
        matchesTarget={matchesTarget}
        errorProperties={errorPropertiesByFile?.[virtualKey]}
        onConfigureSelectOptions={onConfigureSelectOptions}
        onCreateProperty={onCreateProperty}
        onNewPropertyFieldTypeChange={onNewPropertyFieldTypeChange}
        draggedColumn={activeColumn}
        moveTargets={sectionMoveTargets}
        onRemoveColumn={handleRemoveColumn}
        onMoveColumn={handleMoveColumn}
        associationKeyColumns={associationKeyColumns}
      />
    );
  });

  // Pills for every type with no mapped columns. Dragging a column onto one
  // turns it into a full section.
  const unmappedTypesRow =
    pillEntityTypes.length > 0 ? (
      <div className='flex flex-col gap-3'>
        {pillEntityTypes.map(et => {
          const virtualKey = `${virtualPrefix}${et.entity_type_name}`;
          return (
            <EntityTypePill
              key={et.entity_type_name}
              entityTypeName={et.entity_type_name}
              label={
                labelByTypeName[et.entity_type_name] ?? et.entity_type_name
              }
              virtualKey={virtualKey}
            />
          );
        })}
      </div>
    ) : null;

  const intro = isMultiType ? (
    <p className='text-sm text-fg-tertiary'>
      This file contains multiple object types. Map columns to properties for
      each type below. Use the row menu to move or remove columns, or drag a
      column by its handle.
    </p>
  ) : null;

  const dndSections = (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className='space-y-3'>
        {sections}
        {unmappedTypesRow}
        <UnassignedColumnsRow
          unassignedKey={unassignedKey}
          columns={unassignedColumns}
          moveTargets={allMoveTargets}
          onAddTo={handleMoveColumn}
        />
        <InternalColumnsRow columns={internalColumns} />
      </div>
      <DragOverlay dropAnimation={null}>
        {activeColumn ? (
          <DraggableItem variant='chip' className='shadow-lg whitespace-nowrap'>
            {activeColumn}
          </DraggableItem>
        ) : null}
      </DragOverlay>
    </DndContext>
  );

  return (
    <div className='space-y-3'>
      {intro}
      {dndSections}
    </div>
  );
}
